import { DeviceTypesEnum } from '../device';
import { AlarmingEvents, AlarmingEventTrigger, EventsData, WixStoreEventTrigger } from '../events';
import { LocationType } from '../location';
import { VisitorTypes } from '../visitors';

export enum AlarmingEventTypeEnum {
  rageClickStart = 'rageClickStart',
  rageClickEnd = 'rageClickEnd',
  deadClick = 'deadClick',
  scrollStart = 'scrollStart',
  scrollEnd = 'scrollEnd',
  mouseMovesStart = 'mouseMovesStart',
  mouseMovesEnd = 'mouseMovesEnd',
  uTurnStart = 'uTurnStart',
  uTurnEnd = 'uTurnEnd',
  refreshStart = 'refreshStart',
  refreshEnd = 'refreshEnd',

  // one time - after grouping together
  rageClick = 'rageClick',
  mouseMoveAbe = 'mouseMoveAbe',
  scrollAbe = 'scrollAbe',
  uTurn = 'uTurn',
  refresh = 'refresh',
}

export enum RegularEventTypeEnum {
  navigateToOtherPage = 'meta',
  entryPageVisited = 'entryPageVisited',
  scroll = 'scroll',
  focus = 'focus',
  click = 'click',
  dblClick = 'dblClick',
  mouseDown = 'mouseDown',
  mouseUp = 'mouseUp',
  input = 'input',
  touchStart = 'touchStart',
  touchEnd = 'touchEnd',
  mouseMove = 'mouseMove',
  viewportResize = 'viewportResize',
  pageRefresh = 'pageRefresh',
}
const EventTypeEnum = {
  ...AlarmingEventTypeEnum,
  ...RegularEventTypeEnum,
};

export type EventTypeEnum = (typeof EventTypeEnum)[keyof typeof EventTypeEnum];

export type AlarmingEvent = {
  start: number;
  end: number;
  page: string;
  trigger: AlarmingEventTrigger;
  triggerValue: number;
  // Empty string if the ABE event is generated from visits
  recordingId: string;
};

export type WixStoresEvent = {
  recordingId: string;
  timestamp: number;
  page: string;
  eventName: WixStoreEventTrigger;
  visitKey: string;
  eventParams: {
    origin: string;
    userId: string;
    visitorId: string;
    metaSiteId: string;
    contents: {
      category: string;
      currency: string;
      dimension3: string;
      id: string;
      list: string;
      name: string;
      position: number;
      price: number;
    }[];
  };
};

// SSR Events
export enum AlarmingEventType {
  ABE_ONE_TIME = 0,
  ABE_START = 1,
  ABE_END = 2,
}

export type EventType = {
  color: string;
  delay: number;
  details: string;
  tooltipText: string;
  id: string;
  type: number;
  name: EventTypeEnum;
  page: string;
  recordingId: string;
  source?: AlarmingEventTrigger;
};
export type MappedEventType = EventType & { to?: number; eventIndexes: number[] };

export type PageType = {
  url: string;
  type: number;
  duration: number;
  timestamp: number;
  privacyLevel: number;
};

export type RecordingCommentType = {
  id: string;
  text: string;
  timestamp: number;
  typeString: string;
};

export type RecordingType = {
  alarmingEvents: AlarmingEvents;
  events: EventsData;
  browser: string;
  comment?: RecordingCommentType;
  countryCode: string;
  device: DeviceTypesEnum;
  entryPage: string;
  exitPage: string;
  id: string;
  location: LocationType;
  platform: string;
  referrer: null | string;
  screenSize: string;
  sessionExtras: { id: string; seen: boolean; star: boolean };
  sessionStart: number;
  ssrInfo: { duration: number; pagesCount: number; pages: PageType[] };
  ssrStatus: number;
  status: VisitorTypes;
  visitorId: string;
};

export type RecordingsDataType = {
  data: RecordingType[];
  sessionsTotal: number;
};

export type SessionPageFilterValue = string | { contains: string } | { exactMatch: string };

export type CustomEventAttributeFilterValue = { is?: string; contains?: string };

export type CustomEventsFilterValue = Partial<
  Record<'category' | 'action' | 'label' | 'value', CustomEventAttributeFilterValue>
>;

export type RecordingsFiltersType = {
  starred: string;
  watched: string;
  note: string;
  from: number;
  until: number;
  country: {
    is: string;
    isNot: string;
  };
  os: {
    is: string;
    isNot: string;
  };
  browser: {
    is: string;
    isNot: string;
  };
  devices: string[];
  status: number[];
  containsPage: string;
  notContainsPage: string;
  duration: {
    min: number;
    max: number;
  };
  pageCount: {
    min: number;
    max: number;
  };
  entryPage: SessionPageFilterValue;
  exitPage: SessionPageFilterValue;
  funnelDropouts: {
    id: string;
    level: number;
  };
  referrerURLs: {
    is: string[];
    isNot: string[];
  };
  trafficChannels: string[];
  adCampaignLabels: string[];
  tags: Record<string, string>;
  recordingId: string;
  alarmingEvents: AlarmingEventTrigger[];
  customEvents: CustomEventsFilterValue;
};
